import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Search, 
  Calendar, 
  User, 
  Eye,
  ChevronRight,
  FileText,
  Tag
} from 'lucide-react';
import api from '@/utils/api';
import Footer from '@/components/Footer';

const BlogPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedTag, setSelectedTag] = useState(searchParams.get('tag') || '');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
    fetchTags();
  }, [selectedCategory, selectedTag, page]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      let url = `/api/blog?page=${page}&limit=9`;
      if (selectedCategory) url += `&category=${encodeURIComponent(selectedCategory)}`;
      if (selectedTag) url += `&tag=${encodeURIComponent(selectedTag)}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      
      const response = await api.get(url);
      setPosts(response.data.posts);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/blog/categories');
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchTags = async () => {
    try {
      const response = await api.get('/api/blog/tags');
      setTags(response.data.tags);
    } catch (error) {
      console.error('Failed to fetch tags');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPosts();
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category === selectedCategory ? '' : category);
    setSelectedTag('');
    setPage(1);
    if (category && category !== selectedCategory) {
      setSearchParams({ category });
    } else {
      setSearchParams({});
    }
  };

  const handleTagClick = (tag) => {
    setSelectedTag(tag === selectedTag ? '' : tag);
    setSelectedCategory('');
    setPage(1);
    if (tag && tag !== selectedTag) {
      setSearchParams({ tag });
    } else {
      setSearchParams({});
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      <Helmet>
        <title>Blog | Quero Publicar - Dicas de Publicação Científica</title>
        <meta name="description" content="Dicas, tutoriais e novidades sobre publicação científica, QUALIS/CAPES, metodologia e carreira acadêmica." />
      </Helmet>

      {/* Header */}
      <header className="bg-gradient-to-r from-[#002FA7] to-[#0040D0] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Blog</h1>
              <p className="text-white/80">Dicas e novidades sobre publicação científica</p>
            </div>
          </div>
          
          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar artigos..."
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60"
              />
            </div>
            <Button type="submit" variant="secondary">
              Buscar
            </Button>
          </form>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-8 w-full">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {/* Active Filters */}
            {(selectedCategory || selectedTag) && (
              <div className="mb-4 flex items-center gap-2">
                <span className="text-sm text-gray-500">Filtrado por:</span>
                {selectedCategory && (
                  <Badge 
                    className="cursor-pointer bg-[#002FA7]"
                    onClick={() => handleCategoryClick('')}
                  >
                    {selectedCategory} ✕
                  </Badge>
                )}
                {selectedTag && (
                  <Badge 
                    variant="outline"
                    className="cursor-pointer"
                    onClick={() => handleTagClick('')}
                  >
                    #{selectedTag} ✕
                  </Badge>
                )}
              </div>
            )}

            {/* Posts Grid */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full"></div>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-[#E4E4E7]">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nenhum artigo encontrado</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {posts.map((post) => (
                    <article 
                      key={post.post_id}
                      onClick={() => navigate(`/blog/${post.slug}`)}
                      className="bg-white rounded-xl border border-[#E4E4E7] overflow-hidden hover:shadow-lg hover:border-[#002FA7]/30 transition-all cursor-pointer group"
                    >
                      {/* Cover Image */}
                      {post.cover_image ? (
                        <div className="aspect-video overflow-hidden">
                          <img 
                            src={post.cover_image} 
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      ) : (
                        <div className="aspect-video bg-gradient-to-br from-[#002FA7] to-[#0040D0] flex items-center justify-center">
                          <FileText className="h-12 w-12 text-white/50" />
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="p-5">
                        <Badge variant="outline" className="mb-2 text-xs">
                          {post.category}
                        </Badge>
                        
                        <h2 className="font-bold text-lg mb-2 line-clamp-2 group-hover:text-[#002FA7] transition-colors">
                          {post.title}
                        </h2>
                        
                        <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                          {post.excerpt || stripHtml(post.content).substring(0, 120) + '...'}
                        </p>
                        
                        {/* Meta */}
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(post.published_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              {post.views || 0}
                            </span>
                          </div>
                          <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Anterior
                    </Button>
                    <span className="flex items-center px-4 text-sm text-gray-600">
                      Página {page} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Próxima
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:w-72 space-y-6">
            {/* Categories */}
            {categories.length > 0 && (
              <div className="bg-white rounded-lg border border-[#E4E4E7] p-5">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#002FA7]" />
                  Categorias
                </h3>
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryClick(cat.name)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors ${
                        selectedCategory === cat.name 
                          ? 'bg-[#002FA7] text-white' 
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <span>{cat.name}</span>
                      <span className={`text-xs ${selectedCategory === cat.name ? 'text-white/70' : 'text-gray-400'}`}>
                        {cat.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <div className="bg-white rounded-lg border border-[#E4E4E7] p-5">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Tag className="h-4 w-4 text-[#002FA7]" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <button
                      key={tag.name}
                      onClick={() => handleTagClick(tag.name)}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                        selectedTag === tag.name
                          ? 'bg-[#002FA7] text-white border-[#002FA7]'
                          : 'border-gray-300 hover:border-[#002FA7] hover:text-[#002FA7]'
                      }`}
                    >
                      #{tag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-br from-[#002FA7] to-[#0040D0] rounded-lg p-5 text-white">
              <h3 className="font-bold mb-2">Encontre chamadas</h3>
              <p className="text-sm text-white/80 mb-4">
                Busque oportunidades de publicação compatíveis com sua pesquisa.
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="secondary"
                className="w-full"
              >
                Ver chamadas
              </Button>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
